import React, { useRef, useState } from 'react';
import { Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PublicIcon from '@mui/icons-material/Public';
import { EmailBlock } from './types';
import { emailBuilderBlockBackground } from './blockStyles';
import { RowActions } from './RowActions';
import { PublishedAssetPickerDialog } from './PublishedAssetPickerDialog';
import { useModulePermissions } from 'hooks/useModulePermissions';

export const ImageBlock: React.FC<{
  block: EmailBlock;
  isSelected: boolean;
  onSelect: () => void;
  onChange: (patch: Partial<EmailBlock>) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onDragStart: (e: React.DragEvent) => void;
  onDragEnd?: () => void;
}> = ({ block, isSelected, onSelect, onChange: _onChange, onDelete, onDuplicate, onDragStart, onDragEnd }) => {
  const common = {
    px: 2,
    py: 1.5,
    border: '1px solid',
    borderRadius: 2,
    mx: 1,
    borderColor: isSelected ? 'primary.main' : 'divider',
    backgroundColor: emailBuilderBlockBackground,
    cursor: 'move',
    transition: 'background-color 120ms ease, border-color 120ms ease',
    '&:hover': {
      backgroundColor: 'action.hover',
      borderColor: isSelected ? 'primary.main' : 'primary.light'
    }
  } as const;
  return (
    <Box sx={common} draggable onDragStart={onDragStart} onDragEnd={onDragEnd} onClick={onSelect}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="subtitle2" sx={{ mb: 0, wordBreak: 'break-word' }}>
          Image : {block.title || 'Image'}
        </Typography>
        <RowActions onDelete={onDelete} onDuplicate={onDuplicate} />
      </Box>
    </Box>
  );
};

export const ImageBlockDialog: React.FC<{
  open: boolean;
  draft: EmailBlock;
  setDraft: (next: EmailBlock) => void;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ open, draft, setDraft, onCancel, onConfirm }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const { canUseAssetManagement } = useModulePermissions();
  const showDamLibrary = canUseAssetManagement();

  if (draft.type !== 'image') return null;

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        setDraft({ ...draft, imageUrl: result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFileButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleRemoveImage = () => {
    setDraft({ ...draft, imageUrl: '' });
  };

  /**
   * Selecting a published asset switches the block to URL mode so the email
   * references the public CDN copy. Any locally inlined base64 blob is cleared
   * to avoid duplicating the same image source.
   */
  const handlePublishedAssetSelected = ({
    url,
    asset,
  }: {
    url: string;
    asset: { name?: string };
  }) => {
    setDraft({
      ...draft,
      url,
      imageUrl: '',
      title: draft.title?.trim() ? draft.title : asset.name || draft.title,
    });
    setPickerOpen(false);
  };

  return (
    <>
      <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Image</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Title"
              value={draft.title || ''}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              fullWidth
              size="small"
            />

            <TextField
              label="Image URL"
              value={draft.url || ''}
              onChange={(e) => setDraft({ ...draft, url: e.target.value })}
              fullWidth
              size="small"
              placeholder="https://example.com/image.jpg"
            />

            <Box>
              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  mb: 1,
                  flexDirection: { xs: 'column', sm: 'row' },
                }}
              >
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleFileButtonClick}
                  startIcon={<CloudUploadIcon />}
                  sx={{ flex: 1 }}
                >
                  Upload
                </Button>
                {showDamLibrary && (
                  <Button
                    variant="outlined"
                    color="success"
                    onClick={() => setPickerOpen(true)}
                    startIcon={<PublicIcon />}
                    sx={{ flex: 1 }}
                  >
                    DAM Library
                  </Button>
                )}
                {draft.imageUrl && (
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleRemoveImage}
                    sx={{ minWidth: 'auto', px: 2 }}
                  >
                    DELETE
                  </Button>
                )}
              </Box>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              {draft.imageUrl && (
                <Box sx={{ mt: 1, textAlign: 'center' }}>
                  <img
                    src={draft.imageUrl}
                    alt="Preview"
                    style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain' }}
                  />
                </Box>
              )}
            </Box>

            <FormControl fullWidth size="small">
              <InputLabel>Align</InputLabel>
              <Select
                value={draft.align || 'center'}
                onChange={(e) => setDraft({ ...draft, align: e.target.value as any })}
                label="Align"
              >
                <MenuItem value="left">Left</MenuItem>
                <MenuItem value="center">Center</MenuItem>
                <MenuItem value="right">Right</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Width (%)"
              type="number"
              value={draft.width || 100}
              onChange={(e) => setDraft({ ...draft, width: Number(e.target.value) })}
              fullWidth
              size="small"
              inputProps={{ min: 10, max: 100 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="secondary" onClick={onCancel}>Cancel</Button>
          <Button color="success" variant="contained" onClick={onConfirm}>OK</Button>
        </DialogActions>
      </Dialog>

      {showDamLibrary && (
      <PublishedAssetPickerDialog
        open={pickerOpen}
        assetTypeFilter="IMAGE"
        title="Select Published Image Asset"
        primaryActionLabel="Use This Image"
        onCancel={() => setPickerOpen(false)}
        onConfirm={handlePublishedAssetSelected}
      />
      )}
    </>
  );
};


